import { Metamodel } from 'app/model/base/model';
import { FormatWriteSettings } from './format-write-settings';
import { DeltaDataset, format, IAttribute, IValidationContext, IViolation, ModelConstants, sanitizeAndFormatString, StoreSettingPartitionKey } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { CopyActivity } from '../../copy.activity';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { getCloneCompatibleUpsertKeyRequirement, isDeltaCloneCopy } from 'app/authoring/copy-job/wizard/copy-job-delta.util';
import type { CopyJobActivity } from 'app/model/copyjob/copy-job.activity';

export type writeBehaviorValue = 'Insert' | 'Overwrite' | 'Upsert';

@Metamodel.model({
    name: adfModelTypeMap.delta.formatWrite,
    tags: { datasetModel: Metamodel.modelFor(DeltaDataset) },
    validator: DeltaFormatWriteSettings._validateWriteBehavior
})
export class DeltaFormatWriteSettings extends FormatWriteSettings {
    public static readonly WRITE_BEHAVIOR: IAttribute;
    public static readonly PARTITION_OPTION: IAttribute;
    public static readonly PARTITION_KEYS: IAttribute;
    public static readonly APPEND_PARTITION_OPTION: IAttribute;
    public static readonly APPEND_PARTITION_KEYS: IAttribute;
    public static readonly UPSERT_PARTITION_OPTION: IAttribute;
    public static readonly UPSERT_PARTITION_KEYS: IAttribute;
    public static readonly UPSERT_KEYS: IAttribute;
    public static readonly APPLY_VORDER: IAttribute;

    @Metamodel.attribute({
        display: stringResources.TableAction,
        association: 'WRITE_BEHAVIOR',
        templatable: true,
        accepts: ['String']
    })
    public writeBehavior?: writeBehaviorValue = 'Insert';

    @Metamodel.attribute({
        association: 'PARTITION_OPTION',
        templatable: true,
        accepts: ['String'],
        display: stringResources.ModelName_Enable_Sink_Partition
    })
    public enablePartitionOption?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Columns,
        association: 'PARTITION_KEYS',
        templatable: true,
        accepts: ['Array'],
        defaultValue: []
    })
    public partitionNameList?: StoreSettingPartitionKey[] = [];

    @Metamodel.attribute({
        association: 'APPEND_PARTITION_OPTION',
        templatable: true,
        accepts: ['String'],
        display: stringResources.ModelName_Enable_Sink_Partition
    })
    public enableAppendPartitionOption?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Columns,
        association: 'APPEND_PARTITION_KEYS',
        templatable: true,
        accepts: ['Array'],
        defaultValue: []
    })
    public appendPartitionNameList?: StoreSettingPartitionKey[] = [];

    @Metamodel.attribute({
        association: 'UPSERT_PARTITION_OPTION',
        templatable: true,
        accepts: ['String'],
        display: stringResources.ModelName_Enable_Sink_Partition
    })
    public enableUpsertPartitionOption?: boolean;

    @Metamodel.attribute({
        display: stringResources.ModelName_Partition_Columns,
        association: 'UPSERT_PARTITION_KEYS',
        templatable: true,
        accepts: ['Array'],
        defaultValue: []
    })
    public upsertPartitionNameList?: StoreSettingPartitionKey[] = [];

    @Metamodel.attribute({
        display: stringResources.DataFlowDatasetKeyColumns,
        association: 'UPSERT_KEYS',
        templatable: true,
        accepts: ['Array']
    })
    public upsertKeys?: string[];

    @Metamodel.attribute({
        display: stringResources.Dataset_applyVOrder,
        association: 'APPLY_VORDER',
        templatable: true
    })
    public applyVOrder?: boolean;

    public static validateKeyColumns(
        sink: DeltaFormatWriteSettings,
        upsertLabel?: string
    ): IViolation[] | undefined {
        if (isDeltaCloneCopy(sink.owner?.owner as CopyActivity)) {
            return [];
        }
        if (upsertLabel == null) {
            upsertLabel = sink.owner?.owner && Metamodel.modelOf(sink.owner.owner)?.name === ModelConstants.copyJobActivity ?
            stringResources.MergeLabel : stringResources.WriteBehavior_Option_Upsert;
        }

        const activityName = sink.name || (sink.owner?.owner as CopyActivity)?.name || '';
        if (
            (sink.writeBehavior !== 'Upsert') ||
            !stringIsNullOrEmpty(DeltaFormatWriteSettings.UPSERT_KEYS.templateValue(sink))
        ) {
            return [];
        } else if (!sink.upsertKeys || sink.upsertKeys?.length === 0) {
            const activity = sink.owner?.owner as CopyJobActivity;
            const required = (Metamodel.modelOf(activity)?.name === ModelConstants.copyJobActivity
                ? getCloneCompatibleUpsertKeyRequirement(activity) : undefined) ?? true;
            if (!required) {
                return [];
            }
            return [
                {
                    context: sink,
                    message: () => sanitizeAndFormatString(stringResources.RequiresKeyColumn, upsertLabel, activityName),
                    redirectTo: {
                        attribute: DeltaFormatWriteSettings.UPSERT_KEYS,
                        entity: sink
                    }
                }
            ];
        }
        const violations: IViolation[] = [];
        const uniqueNames: Set<string> = new Set();
        let hasEmptyString = false;
        sink.upsertKeys?.forEach((key) => {
            if (key === '') {
                if (!hasEmptyString) {
                    violations.push({
                        context: sink,
                        message: () => sanitizeAndFormatString(stringResources.RequiresKeyColumn, upsertLabel, activityName),
                        redirectTo: {
                            attribute: DeltaFormatWriteSettings.UPSERT_KEYS,
                            entity: sink
                        }
                    });
                    hasEmptyString = true;
                }
            } else if (uniqueNames.has(key)) {
                violations.push({
                    context: sink,
                    message: () => sanitizeAndFormatString(stringResources.DupKeyColumn, key),
                    redirectTo: {
                        attribute: DeltaFormatWriteSettings.UPSERT_KEYS,
                        entity: sink
                    }
                });
            } else {
                uniqueNames.add(key);
            }
        });
        return violations;
    }

    public static validatePartitionNames(sink: DeltaFormatWriteSettings): IViolation[] | undefined {
        const nameSet = new Set<string>();
        const writeBehavior = ValueAdapter.toResource(sink, DeltaFormatWriteSettings.WRITE_BEHAVIOR);

        const enablePartitionOption =
            writeBehavior === 'Insert'
                ? ValueAdapter.toResource(sink, DeltaFormatWriteSettings.APPEND_PARTITION_OPTION)
                : writeBehavior === 'Upsert'
                    ? ValueAdapter.toResource(sink, DeltaFormatWriteSettings.UPSERT_PARTITION_OPTION)
                    : ValueAdapter.toResource(sink, DeltaFormatWriteSettings.PARTITION_OPTION);
        const partitionNameList =
            writeBehavior === 'Insert'
                ? ValueAdapter.toResource(sink, DeltaFormatWriteSettings.APPEND_PARTITION_KEYS)
                : writeBehavior === 'Upsert'
                    ? ValueAdapter.toResource(sink, DeltaFormatWriteSettings.UPSERT_PARTITION_KEYS)
                    : ValueAdapter.toResource(sink, DeltaFormatWriteSettings.PARTITION_KEYS);

        if (enablePartitionOption && Array.isArray(partitionNameList)) {
            // if enablePartitionOption is true, then partitionNameList must exist
            if (enablePartitionOption === true && arrayIsNullOrEmpty(partitionNameList)) {
                return [
                    {
                        context: sink.partitionNameList,
                        message: () => stringResources.Partition_Name_Not_Allow_EmptyName
                    }
                ];
            }
            // partitionNameList cannot contain empty values, as long as enablePartitionOption is not false
            for (const partitionName of partitionNameList) {
                const templated = ValueAdapter.isTemplated(partitionName, StoreSettingPartitionKey.PARTITITON_KEY);
                const value = templated
                    ? ValueAdapter.toResource(partitionName, StoreSettingPartitionKey.PARTITITON_KEY).value
                    : partitionName.partitionKey;
                if (!value) {
                    return [
                        {
                            context: sink.partitionNameList,
                            message: () => stringResources.Partition_Name_Not_Allow_EmptyName
                        }
                    ];
                }

                if (nameSet.has(value)) {
                    return [
                        {
                            context: sink.partitionNameList,
                            message: () => stringResources.Partition_Name_Not_Allow_DuplicateName
                        }
                    ];
                }
                nameSet.add(value);
            }

            if (writeBehavior === 'Upsert' && sink.upsertKeys?.length > 0) {
                const overlappedKeys = partitionNameList?.filter((partitionName) =>
                    sink.upsertKeys.includes(partitionName.partitionKey)
                )?.map((item) => item.partitionKey);

                if (overlappedKeys?.length > 0) {
                    return [
                        {
                            context: sink.partitionNameList,
                            message: () => format(stringResources.Partition_Name_Cannot_Be_In_Key_Columns, stringResources.Upsert_Method, overlappedKeys.join(', '))
                        }
                    ];
                }
            }
        }
        const copyActivity = sink.owner?.owner as CopyActivity;

        if (nameSet.size > 0 && nameSet.size === copyActivity?.cacheSourceSchema?.length) {
            return [
                {
                    context: sink.partitionNameList,
                    message: () => stringResources.Partition_Names_Not_Allow_All_Selected
                }
            ];
        }
        return undefined;
    }

    private static async _validateWriteBehavior(
        activity: DeltaFormatWriteSettings,
        context: IValidationContext
    ): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];
        violations.push(...(DeltaFormatWriteSettings.validateKeyColumns(activity) || []));
        violations.push(...(DeltaFormatWriteSettings.validatePartitionNames(activity) || []));

        return violations;
    }
}
